/**
 * useCurrencies Hook
 *
 * Fetches available currencies from the CurrencyRates microservice
 * and provides the organization's base currency.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'

import { listCurrencies, type CurrencyInfo } from '../services/currency-service'
import { getOrganization } from '../services/organization-service'
import { useOrganization } from '../stores/organization-store'
import { useSettings } from '../stores/settings-store'

// ============================================================================
// Types
// ============================================================================

export interface CurrencyOption {
  value: string
  label: string
  symbol?: string
  decimalPlaces: number
}

// ============================================================================
// Fallback currencies when the CurrencyRates service is unavailable
// ============================================================================

const FALLBACK_CURRENCIES: CurrencyInfo[] = [
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['IN'] },
  { code: 'USD', name: 'US Dollar', symbol: '$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['US'] },
  { code: 'EUR', name: 'Euro', symbol: '€', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['EU'] },
  { code: 'GBP', name: 'British Pound', symbol: '£', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['GB'] },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['AE'] },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['SG'] },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['AU'] },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['CA'] },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', decimalPlaces: 0, isActive: true, isCrypto: false, countryCodes: ['JP'] },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['CH'] },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['CN'] },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['SA'] },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['MY'] },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['TH'] },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['ZA'] },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['NZ'] },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['HK'] },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['SE'] },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['NO'] },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['DK'] },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', decimalPlaces: 0, isActive: true, isCrypto: false, countryCodes: ['KR'] },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['BR'] },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['MX'] },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimalPlaces: 0, isActive: true, isCrypto: false, countryCodes: ['ID'] },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['PH'] },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['PL'] },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['TR'] },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['RU'] },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['BD'] },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['LK'] },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: 'Rs', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['NP'] },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: 'Rs', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['PK'] },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['QA'] },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', decimalPlaces: 3, isActive: true, isCrypto: false, countryCodes: ['KW'] },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BD', decimalPlaces: 3, isActive: true, isCrypto: false, countryCodes: ['BH'] },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', decimalPlaces: 3, isActive: true, isCrypto: false, countryCodes: ['OM'] },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['EG'] },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['KE'] },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['NG'] },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', decimalPlaces: 2, isActive: true, isCrypto: false, countryCodes: ['GH'] },
]

// ============================================================================
// Module-level cache (shared across all hook instances)
// ============================================================================

let cachedCurrencies: CurrencyInfo[] | null = null
let cacheTimestamp = 0
const CACHE_TTL = 10 * 60 * 1000 // 10 minutes

function isCacheValid(): boolean {
  return cachedCurrencies !== null && Date.now() - cacheTimestamp < CACHE_TTL
}

// ============================================================================
// Standalone fetch helper (extracted to keep useCurrencies under max lines)
// ============================================================================

async function fetchCurrenciesFromApi(): Promise<{
  currencies: CurrencyInfo[]
  error: string | null
}> {
  if (isCacheValid()) {
    return { currencies: cachedCurrencies ?? [], error: null }
  }

  const response = await listCurrencies()
  if (response.data && response.data.length > 0) {
    cachedCurrencies = response.data
    cacheTimestamp = Date.now()
    return { currencies: response.data, error: null }
  }

  // Use fallback currencies when API is unavailable or returns empty
  cachedCurrencies = FALLBACK_CURRENCIES
  cacheTimestamp = Date.now()
  const errorMsg = response.error ? (response.error.message ?? 'Using offline currency list') : null
  return { currencies: FALLBACK_CURRENCIES, error: errorMsg }
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all available currencies. Results are cached in memory.
 */
export function useCurrencies(): {
  currencies: CurrencyOption[]
  currencyMap: Map<string, CurrencyInfo>
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
} {
  const [currencies, setCurrencies] = useState<CurrencyInfo[]>(cachedCurrencies ?? FALLBACK_CURRENCIES)
  const [isLoading, setIsLoading] = useState(!isCacheValid())
  const [error, setError] = useState<string | null>(null)

  const fetchCurrencies = useCallback(async () => {
    setIsLoading(true)
    const result = await fetchCurrenciesFromApi()
    setCurrencies(result.currencies)
    setError(result.error)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    void fetchCurrencies()
  }, [fetchCurrencies])

  const options: CurrencyOption[] = useMemo(
    () =>
      currencies
        .filter(c => c.isActive && !c.isCrypto)
        .map(c => ({
          value: c.code,
          label: `${c.code} — ${c.name}`,
          symbol: c.symbol,
          decimalPlaces: c.decimalPlaces,
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [currencies]
  )

  const currencyMap = useMemo(
    () => new Map(currencies.map(c => [c.code, c])),
    [currencies]
  )

  return { currencies: options, currencyMap, isLoading, error, refetch: fetchCurrencies }
}

/**
 * Get the organization's base currency from settings.
 */
export function useBaseCurrency(): string {
  const { settings } = useSettings()
  return settings.baseCurrency || 'INR'
}

/**
 * Returns only the currencies enabled for this organization.
 * Falls back to all currencies if none are configured yet.
 */
export function useEnabledCurrencies(): {
  currencies: CurrencyOption[]
  isLoading: boolean
} {
  const { organizationId } = useOrganization()
  const { currencies: allCurrencies, isLoading: isCurrenciesLoading } = useCurrencies()
  const [enabledCodes, setEnabledCodes] = useState<Set<string> | null>(null)
  const [isOrgLoading, setIsOrgLoading] = useState(true)

  useEffect(() => {
    async function load(): Promise<void> {
      const res = await getOrganization(organizationId)
      if (res.data?.enabledCurrencies && res.data.enabledCurrencies.length > 0) {
        setEnabledCodes(new Set(res.data.enabledCurrencies))
      }
      setIsOrgLoading(false)
    }
    void load()
  }, [organizationId])

  const filtered = useMemo(() => {
    // If no enabled currencies configured, show all
    if (!enabledCodes) {return allCurrencies}
    return allCurrencies.filter((c) => enabledCodes.has(c.value))
  }, [allCurrencies, enabledCodes])

  return { currencies: filtered, isLoading: isCurrenciesLoading || isOrgLoading }
}
